import React, { useCallback, useContext, useMemo, useState } from 'react';
import styled from 'styled-components';
import { ModalTransition } from '@atlaskit/modal-dialog';

import BaseViewsContext from 'contexts/BaseViewsContext';
import DateInputContext from 'contexts/DateInputContext';
import FilterPlatesContext from 'contexts/FilterPlatesContext';
import { InsightsChatContext } from 'contextProviders/InsightsChatProvider';
import { getDrillDownFieldName } from 'hooks/useDrillDownFieldName';
import useDateScope from 'hooks/useDateScope';
import { getDrillDownDescription } from 'components/FilterPlates/FilterPlate';
import CloseModal from 'components/CloseModal';
import DateInput from 'components/DateInput';
import GadgetFormContainer from 'components/GadgetForm';
import FilterPlates from 'components/FilterPlates';
import Row from 'components/Common/Row';
import Button from 'kingpin/atoms/Button';
import Typography from 'kingpin/atoms/Typography';
import usePopup from 'hooks/usePopup';
import isV5ChartDef from 'types/visTypeCheckers/isV5ChartDef';
import { InsightsChartSpec } from 'services/streamInsightsQuery';

import SaveInsightsChartModal from '../saveChart/SaveInsightsChartModal';
import { InsightsChartSaveOverrides } from '../saveChart/useSaveInsightsChart';
import buildAskAboutViewQuestion from './buildAskAboutViewQuestion';
import ExploreChartPreview from './ExploreChartPreview';

const Layout = styled.div`
  display: flex;
  flex-direction: column;
  height: 80vh;
  position: relative;
  padding: 16px 24px 24px 24px;
  box-sizing: border-box;
`;

const Header = styled(Row)`
  margin-bottom: 12px;
  padding-right: 40px; /* clear of the absolute close button */
`;

const Toolbar = styled(Row)`
  gap: 12px;
  margin-bottom: 12px;
  flex-wrap: wrap;
`;

// The rail and the chart sit side by side; the chart keeps the lion's share of
// a 95vw modal, which is the whole point of exploring here rather than in the
// chat bubble.
const Body = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  gap: 16px;
`;

const ConfigRail = styled.div`
  width: 320px;
  flex-shrink: 0;
  overflow-y: auto;
  border-right: 1px solid var(--color-border-secondary);
  padding-right: 16px;
`;

const ChartArea = styled.div`
  flex: 1;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

// The inside of the Explore modal. Sits INSIDE the provider stack so it can read
// the live date range and filters — DateInputProvider owns the date range
// internally rather than it being lifted into Explore's state.
const ExploreChartContent = ({
  spec,
  chartDef,
  setChartDef,
  onClose,
}: {
  spec: InsightsChartSpec;
  chartDef?: VisualisationDefinition;
  setChartDef: React.Dispatch<
    React.SetStateAction<VisualisationDefinition | undefined>
  >;
  onClose: () => void;
}) => {
  const {
    drillDowns,
    scope,
    drillDownsAsFixed,
    scopeAsFixed,
    setDrillDowns,
    setScope,
  } = useContext(FilterPlatesContext);
  const { dateField, dateRange, relativeDateRange, advancedRelativeDateRange } =
    useContext(DateInputContext);
  const { baseViews } = useContext(BaseViewsContext);
  const { sendQuestion, isLoading } = useContext(InsightsChatContext);
  // Resolves a relative range ("Last month") to concrete ISO dates, so the
  // question can't leave the agent guessing a boundary.
  const dateScope = useDateScope({});

  // Collapsed by default: interrogating comes first, and the form drags in a
  // second DateInputProvider plus the whole gadget-builder machinery, which
  // there's no reason to pay for on open.
  const [isConfigOpen, setIsConfigOpen] = useState(false);
  const { isOpen: isSaveOpen, open: openSave, close: closeSave } = usePopup();

  // What the user actually explored to, so the saved gadget reproduces THIS
  // view rather than the chart the agent originally drew.
  const saveOverrides: InsightsChartSaveOverrides = useMemo(
    () => ({
      chartDef,
      drillDowns,
      scope,
      dateField,
      dateRange,
      relativeDateRange,
      advancedRelativeDateRange,
    }),
    [
      chartDef,
      drillDowns,
      scope,
      dateField,
      dateRange,
      relativeDateRange,
      advancedRelativeDateRange,
    ],
  );

  const hasFilters = drillDownsAsFixed.length > 0 || scopeAsFixed.length > 0;

  const onClear = useCallback(() => {
    setDrillDowns([]);
    setScope([]);
  }, [setDrillDowns, setScope]);

  const onAsk = useCallback(() => {
    const filterDescriptions = [...drillDownsAsFixed, ...scopeAsFixed].map(
      (filter) =>
        getDrillDownDescription(
          filter,
          getDrillDownFieldName(filter, baseViews),
        ),
    );

    sendQuestion(
      buildAskAboutViewQuestion({
        chartTitle: chartDef?.name,
        breakdownLabel: isV5ChartDef(chartDef)
          ? chartDef.dimensionA?.field
          : undefined,
        trendInterval: isV5ChartDef(chartDef)
          ? chartDef.trendByCalendarInterval
          : undefined,
        startDate: dateScope.startDate || '',
        endDate: dateScope.endDate || '',
        filterDescriptions,
      }),
    );
    // The answer streams into the chat behind this modal, so get out of the way.
    onClose();
  }, [
    drillDownsAsFixed,
    scopeAsFixed,
    baseViews,
    chartDef,
    dateScope,
    sendQuestion,
    onClose,
  ]);

  return (
    <Layout data-testid="explore-chart-content">
      <CloseModal close={onClose} />
      <Header spaceBetween centerAlign>
        <Typography.Header type="H5">{chartDef?.name}</Typography.Header>
        <Row centerAlign style={{ gap: 8 }}>
          <Button
            size="Small"
            type="Ghost"
            label={isConfigOpen ? 'Hide config' : 'Configure'}
            onClick={() => setIsConfigOpen((current) => !current)}
            testId="explore-toggle-config"
          />
          <Button
            size="Small"
            type="Ghost"
            label="Save as gadget"
            onClick={openSave}
            // With no chart def (the config rail can clear the gadget type)
            // the save would fall back to the agent's ORIGINAL chart, quietly
            // persisting something other than what's on screen.
            isDisabled={!chartDef}
            isDisabledMessage={
              chartDef ? undefined : 'Pick a gadget type to save this chart'
            }
            testId="explore-save-chart"
          />
          <Button
            size="Small"
            type="Ghost"
            label="Ask about this view"
            onClick={onAsk}
            // sendQuestion silently no-ops mid-turn, which would look like a
            // dead button.
            isDisabled={isLoading}
            isDisabledMessage={
              isLoading ? 'Wait for the current answer to finish' : undefined
            }
            testId="explore-ask-about-view"
          />
        </Row>
      </Header>
      <Toolbar centerAlign>
        <DateInput isDashboardCard />
        {/* Two plate rows: the chart's scope (the agent's filters, plus
            anything the user adds — scope always reaches the query) and the
            drill-downs picked up by clicking into the chart. FilterPlates
            renders one set or the other, so both are mounted; only the scope
            row carries the "Add Filter" button. */}
        <FilterPlates isScope isDashboardCard hideInheritedFilters />
        <FilterPlates
          isDashboardCard
          hideInheritedFilters
          hideAddFilterButton
        />
        {hasFilters && (
          <Button
            size="Small"
            type="Ghost"
            label="Clear"
            onClick={onClear}
            testId="explore-clear-filters"
          />
        )}
      </Toolbar>
      <Body>
        {isConfigOpen && (
          <ConfigRail data-testid="explore-config-rail">
            <GadgetFormContainer
              selected={chartDef}
              onChange={setChartDef}
              isEditing
            />
          </ConfigRail>
        )}
        <ChartArea>
          {chartDef && <ExploreChartPreview chartDef={chartDef} />}
        </ChartArea>
      </Body>
      <ModalTransition>
        {isSaveOpen && (
          <SaveInsightsChartModal
            spec={spec}
            overrides={saveOverrides}
            onClose={closeSave}
          />
        )}
      </ModalTransition>
    </Layout>
  );
};

export default ExploreChartContent;
